/**
 * ========================================
 * TICKET SERVICE
 * ========================================
 * API calls for the Ticket Management module.
 * Endpoints: /api/v1/tickets/*
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';
import type {
  Ticket,
  TicketAttachmentUpload,
  TicketCategory,
  TicketFilters,
  TicketCreateRequest,
  TicketUpdateRequest,
  TicketReplyRequest,
  AssignTicketRequest,
  ChangeStatusRequest,
  CloseTicketRequest,
  ReopenTicketRequest,
  TicketConversationEntry,
  TicketAuditLogEntry,
  TicketDashboardCounts,
  UserSummary,
} from '@/types/ticket';

interface BackendEnvelope<T> {
  status: string;
  status_code?: number;
  message: string;
  data: T;
  summary?: { total: number; page: number; page_size: number };
}

export interface TicketListResult {
  items: Ticket[];
  total: number;
  page: number;
  page_size: number;
}

class TicketService {
  private baseUrl = '/api/v1/tickets';

  async list(filters?: TicketFilters & { page?: number; page_size?: number }): Promise<TicketListResult> {
    try {
      const response = await apiClient.get<BackendEnvelope<Ticket[]>>(this.baseUrl, { params: filters || {} });
      const summary = response.data.summary;
      return {
        items: response.data.data,
        total: summary?.total ?? response.data.data.length,
        page: summary?.page ?? 1,
        page_size: summary?.page_size ?? response.data.data.length,
      };
    } catch (error) {
      handleAPIError(error);
    }
  }

  async listMy(page = 1, pageSize = 20): Promise<TicketListResult> {
    try {
      const response = await apiClient.get<BackendEnvelope<Ticket[]>>(`${this.baseUrl}/my`, { params: { page, page_size: pageSize } });
      const summary = response.data.summary;
      return { items: response.data.data, total: summary?.total ?? response.data.data.length, page: summary?.page ?? 1, page_size: summary?.page_size ?? response.data.data.length };
    } catch (error) {
      handleAPIError(error);
    }
  }

  async listAssigned(page = 1, pageSize = 20): Promise<TicketListResult> {
    try {
      const response = await apiClient.get<BackendEnvelope<Ticket[]>>(`${this.baseUrl}/assigned`, { params: { page, page_size: pageSize } });
      const summary = response.data.summary;
      return { items: response.data.data, total: summary?.total ?? response.data.data.length, page: summary?.page ?? 1, page_size: summary?.page_size ?? response.data.data.length };
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getById(id: string): Promise<Ticket> {
    try {
      const response = await apiClient.get<BackendEnvelope<Ticket>>(`${this.baseUrl}/${id}`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async listCategories(): Promise<TicketCategory[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<TicketCategory[]>>(`${this.baseUrl}/categories`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async listSupportUsers(): Promise<UserSummary[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<UserSummary[]>>(`${this.baseUrl}/support-users`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async dashboard(): Promise<TicketDashboardCounts> {
    try {
      const response = await apiClient.get<BackendEnvelope<TicketDashboardCounts>>(`${this.baseUrl}/dashboard`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getConversation(id: string): Promise<TicketConversationEntry[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<TicketConversationEntry[]>>(`${this.baseUrl}/${id}/conversation`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async getAuditLog(id: string): Promise<TicketAuditLogEntry[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<TicketAuditLogEntry[]>>(`${this.baseUrl}/${id}/audit-log`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async uploadAttachment(file: File): Promise<TicketAttachmentUpload> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<BackendEnvelope<TicketAttachmentUpload>>(
        `${this.baseUrl}/upload-attachment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async create(payload: TicketCreateRequest): Promise<Ticket> {
    try {
      const response = await apiClient.post<BackendEnvelope<Ticket>>(this.baseUrl, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async update(id: string, payload: TicketUpdateRequest): Promise<Ticket> {
    try {
      const response = await apiClient.put<BackendEnvelope<Ticket>>(`${this.baseUrl}/${id}`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async reply(id: string, payload: TicketReplyRequest): Promise<TicketConversationEntry> {
    try {
      const response = await apiClient.post<BackendEnvelope<TicketConversationEntry>>(`${this.baseUrl}/${id}/reply`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async assign(id: string, payload: AssignTicketRequest): Promise<Ticket> {
    try {
      const response = await apiClient.post<BackendEnvelope<Ticket>>(`${this.baseUrl}/${id}/assign`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async changeStatus(id: string, payload: ChangeStatusRequest): Promise<Ticket> {
    try {
      const response = await apiClient.post<BackendEnvelope<Ticket>>(`${this.baseUrl}/${id}/status`, payload);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async close(id: string, payload?: CloseTicketRequest): Promise<Ticket> {
    try {
      const response = await apiClient.post<BackendEnvelope<Ticket>>(`${this.baseUrl}/${id}/close`, payload || {});
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async reopen(id: string, payload?: ReopenTicketRequest): Promise<Ticket> {
    try {
      const response = await apiClient.post<BackendEnvelope<Ticket>>(`${this.baseUrl}/${id}/reopen`, payload || {});
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }
}

export const ticketService = new TicketService();
export default ticketService;
